'use client';

import React from 'react';
import { Container } from 'components/shared';
import { useTranslations } from 'next-intl';
import { UHLesMillsTitle } from './typography';
import ImagePlaceholder from './ImagePlaceholder';
import { getLesMillsContent } from './content';
import styles from './WhatYouGet.module.css';

const SpeakerMuted = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 10v4a1 1 0 0 0 1 1h2.5l4.5 3.5a.5.5 0 0 0 .8-.4V5.9a.5.5 0 0 0-.8-.4L7.5 9H5a1 1 0 0 0-1 1Z"
      fill="currentColor"
    />
    <path
      d="m16 9 5 6m0-6-5 6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const SpeakerOn = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 10v4a1 1 0 0 0 1 1h2.5l4.5 3.5a.5.5 0 0 0 .8-.4V5.9a.5.5 0 0 0-.8-.4L7.5 9H5a1 1 0 0 0-1 1Z"
      fill="currentColor"
    />
    <path
      d="M16 9.2a4 4 0 0 1 0 5.6M18.5 6.5a7.5 7.5 0 0 1 0 11"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const WhatYouGet = () => {
  const t = useTranslations('uhXLesMills');
  const { whatYouGetItems, whatYouGetVideo } = React.useMemo(
    () => getLesMillsContent(t),
    [t],
  );
  const sectionRef = React.useRef<HTMLElement | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [visible, setVisible] = React.useState(false);
  // `nearViewport` is true well before `visible` so the browser can start
  // fetching the first bytes of the video over a range request while the
  // user is still scrolling towards the section. Nothing is downloaded
  // until then — the <video> element has no `src` attribute.
  const [nearViewport, setNearViewport] = React.useState(false);
  const [muted, setMuted] = React.useState(true);

  const toggleMute = React.useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      const video = videoRef.current;
      if (video) {
        video.muted = next;
        if (!next && video.paused) {
          video.play().catch(() => {
            /* ignore — user can retry */
          });
        }
      }
      return next;
    });
  }, []);

  React.useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    // Fade-in / animation trigger — needs real intersection.
    const inViewObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            inViewObserver.disconnect();
            break;
          }
        }
      },
      { threshold: 0.15 },
    );

    // Download-start trigger — fires earlier (600px below the fold)
    // so the video is already buffering by the time the user arrives.
    const proximityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setNearViewport(true);
            proximityObserver.disconnect();
            break;
          }
        }
      },
      { rootMargin: '600px 0px' },
    );

    inViewObserver.observe(node);
    proximityObserver.observe(node);

    return () => {
      inViewObserver.disconnect();
      proximityObserver.disconnect();
    };
  }, []);

  React.useEffect(() => {
    if (!visible) return;
    if (!whatYouGetVideo.src) return;
    const video = videoRef.current;
    if (!video) return;

    const timer = window.setTimeout(() => {
      video.play().catch(() => {
        /* autoplay rejected — leave poster visible */
      });
    }, whatYouGetVideo.playDelayMs);

    return () => window.clearTimeout(timer);
  }, [visible, whatYouGetVideo.playDelayMs, whatYouGetVideo.src]);

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      role="region"
      aria-labelledby="lm-whatyouget-heading"
    >
      <Container>
        <div className={styles.inner}>
          <UHLesMillsTitle
            className={styles.title}
            id="lm-whatyouget-heading"
            $mobileColor="#fff"
            $desktopColor="#fff"
          >
            {t('whatYouGet.heading')}
          </UHLesMillsTitle>
          <div className={styles.grid}>
            {whatYouGetItems.map((it, idx) => (
              <div
                className={styles.item}
                key={it.title}
                data-visible={visible ? 'true' : 'false'}
                style={
                  {
                    ['--fade-delay' as string]: `${idx * 120}ms`,
                  } as React.CSSProperties
                }
              >
                <div className={styles.iconWrapper}>
                  {it.icon ? (
                    <img
                      className={styles.iconImage}
                      src={it.icon}
                      alt={it.iconLabel}
                    />
                  ) : (
                    <ImagePlaceholder
                      label={it.iconLabel}
                      ratio="1/1"
                      radius={10}
                      dark
                    />
                  )}
                </div>
                <div className={styles.textStack}>
                  <h3 className={styles.itemTitle}>{it.title}</h3>
                  <p className={styles.itemCaption}>{it.caption}</p>
                </div>
              </div>
            ))}
          </div>
          <div
            className={styles.videoFrame}
            data-visible={visible ? 'true' : 'false'}
            aria-label={t('a11y.overviewVideoLabel')}
          >
            {whatYouGetVideo.src ? (
              <video
                className={styles.video}
                ref={videoRef}
                src={nearViewport ? whatYouGetVideo.src : undefined}
                poster={whatYouGetVideo.poster}
                muted={muted}
                loop
                playsInline
                preload="none"
              />
            ) : (
              <div className={styles.placeholderInside}>
                {t('common.videoPlaceholder')}
              </div>
            )}
            <button
              className={styles.muteButton}
              type="button"
              onClick={toggleMute}
              aria-label={muted ? t('a11y.unmuteVideo') : t('a11y.muteVideo')}
              aria-pressed={!muted}
            >
              {muted ? <SpeakerMuted /> : <SpeakerOn />}
            </button>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default WhatYouGet;
